import { FC, useState } from 'react';

import { Divider, Paper } from '@mui/material';

import FulfilmentHistory from '@/shared/types/fulfilment/history.type';
import Order from '@/shared/types/order/order.type';
import OrderProduct from '@/shared/types/order/orderProduct.type';

import FulfilmentOrderHistory from './components/FulfilmentOrderHistory';
import ProductDetailsList from './components/ProductDetailsList';
import Title from './components/Title';
import fulfilmentHistory from './fulfilmentHistory.stub.json';

type HistoryPanel = {
  id: string;
  position: { x: number; y: number };
  product: OrderProduct;
  isActive: boolean;
};

type OrderDetailsProps = {
  order: Order;
};

const OrderDetails: FC<OrderDetailsProps> = ({ order }) => {
  const [historyPanels, setHistoryPanels] = useState<HistoryPanel[]>([]);

  const { data: historyList } = fulfilmentHistory as {
    data: FulfilmentHistory[];
  };

  const sortedHistoryByDateModified = historyList.sort(
    (a, b) =>
      new Date(b.ts_modified).getTime() - new Date(a.ts_modified).getTime(),
  );

  const onToggleHistoryPanel = (product: OrderProduct, rect: DOMRect) => {
    const componentWidth = 488;
    let leftPosition = rect.x + rect.width + 10;

    if (leftPosition + componentWidth > window.innerWidth) {
      leftPosition = window.innerWidth - componentWidth - 15;
    }
    const topPosition = rect.y + 10;

    const newPanel: HistoryPanel = {
      id: product.product_id,
      position: { x: leftPosition, y: topPosition },
      product,
      isActive: false,
    };

    const isPanelExists = historyPanels.some(
      (panel) => panel.id === newPanel.id,
    );

    if (isPanelExists) {
      setHistoryPanels((prev) =>
        prev.filter((prevPanel) => prevPanel.id !== newPanel.id),
      );
    }

    setHistoryPanels((prev) => [...prev, newPanel]);
  };

  const onSetActivePanel = (id: string, dragging: boolean) => {
    setHistoryPanels((prev) =>
      prev.map((prevPanel) =>
        prevPanel.id === id ? { ...prevPanel, isActive: dragging } : prevPanel,
      ),
    );
  };

  const onClosePanel = (id: string) => {
    setHistoryPanels((prev) => prev.filter((prevPanel) => prevPanel.id !== id));
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Title order={order} />
        <Divider sx={{ my: 1 }} />
        <ProductDetailsList
          activeHistoryPanelItem={{
            id: historyPanels.find((panel) => panel.isActive)?.id || '',
            isActive: historyPanels.some((panel) => panel.isActive),
          }}
          history={sortedHistoryByDateModified[0]}
          order={order}
          onToggleHistoryPanel={onToggleHistoryPanel}
        />
      </Paper>

      {historyPanels.map((panel) => (
        <FulfilmentOrderHistory
          key={panel.id}
          history={sortedHistoryByDateModified}
          product={panel.product}
          initialPos={panel.position}
          getDragging={(dragging) => onSetActivePanel(panel.id, dragging)}
          onClose={() => onClosePanel(panel.id)}
        />
      ))}
    </>
  );
};

export default OrderDetails;
